import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Warning, UserCircle } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
    <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden" data-testid="account-approval-page">
      <div className="border-b-4 border-primary px-6 py-4 flex items-center gap-3">
        <img src="/nucleus-logo.png" alt="" className="h-9 w-9 object-contain" />
        <span className="text-lg font-black tracking-tight">New Account Approval</span>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const Row = ({ label, value, testid }) => (
  <div><span className="text-zinc-400 text-xs uppercase tracking-wide">{label}</span><div className="font-medium break-all" data-testid={testid}>{value || "—"}</div></div>
);

export default function AccountApproval() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const preset = sp.get("action") === "reject" ? "reject" : "approve";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/account-approvals/${token}`).then((r) => setData(r.data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [token]);

  const decide = async (decision) => {
    setSubmitting(true);
    try { setResult((await api.post(`/account-approvals/${token}/decide`, { decision })).data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (!data && !error) return <Shell><div className="py-10"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div></Shell>;
  if (error && !data) return <Shell><div className="py-8 text-center" data-testid="account-approval-error"><Warning size={40} className="mx-auto text-amber-500 mb-3" /><p className="text-sm text-zinc-600">{error}</p></div></Shell>;

  if (result) {
    const ok = result.decision === "approve";
    return (
      <Shell>
        <div className="py-6 text-center" data-testid="account-approval-result">
          {ok ? <CheckCircle size={52} weight="fill" className="mx-auto text-emerald-500 mb-3" /> : <XCircle size={52} weight="fill" className="mx-auto text-rose-500 mb-3" />}
          <h2 className="text-xl font-bold">{ok ? "Account approved" : "Account rejected"}</h2>
          <p className="text-sm text-zinc-600 mt-1"><strong>{result.email}</strong> has been {ok ? "approved and can now sign in" : "rejected"}. They've been emailed.</p>
        </div>
      </Shell>
    );
  }

  const closed = data.status !== "pending" || data.superseded;
  return (
    <Shell>
      <div className="flex items-start gap-3 mb-5">
        <UserCircle size={26} className="text-primary mt-0.5" />
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold">Account request</p>
          <h2 className="text-xl font-black break-all" data-testid="account-approval-name">{data.name || data.email}</h2>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm mb-5">
        <Row label="Email" value={data.email} testid="account-approval-email" />
        <Row label="Company" value={data.company} />
        <Row label="Sign-in" value={data.auth_provider === "google" ? "Google" : "Email"} />
        <Row label="Requested" value={(data.created_at || "").slice(0, 10)} />
      </div>
      {closed ? (
        <p className="text-sm text-zinc-600 flex items-center gap-2" data-testid="account-approval-closed">
          <Warning size={18} className="text-amber-500" />
          {data.superseded ? "A newer approval request was sent for this account." : `This account is already ${data.status}. No action needed.`}
        </p>
      ) : (
        <>
          {error && <p className="text-sm text-rose-600 mb-3" data-testid="account-approval-action-error">{error}</p>}
          <div className={`flex gap-3 ${preset === "reject" ? "flex-row-reverse" : ""}`}>
            <Button onClick={() => decide("approve")} disabled={submitting} variant={preset === "reject" ? "outline" : "default"} className={`rounded-sm flex-1 ${preset === "reject" ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`} data-testid="account-approve-btn">
              <CheckCircle size={18} weight="bold" className="mr-1.5" /> Approve
            </Button>
            <Button onClick={() => decide("reject")} disabled={submitting} variant={preset === "reject" ? "default" : "outline"} className={`rounded-sm flex-1 ${preset === "reject" ? "bg-rose-600 hover:bg-rose-700 text-white" : "border-rose-300 text-rose-700 hover:bg-rose-50"}`} data-testid="account-reject-btn">
              <XCircle size={18} weight="bold" className="mr-1.5" /> Reject
            </Button>
          </div>
        </>
      )}
    </Shell>
  );
}
